import * as React from "react";
import { formatCreditsUsd } from "@/lib/format";
import { cn } from "@/lib/utils";

export interface BudgetOrbitProps extends React.HTMLAttributes<HTMLDivElement> {
  used: number; // credits
  projected?: number; // credits
  reserved?: number; // credits
  limit: number; // credits
  showLegend?: boolean;
}

/**
 * BudgetOrbit renders three concentric progress rings representing
 * used, projected, and reserved spend against the monthly budget limit.
 */
export function BudgetOrbit({
  used,
  projected = 0,
  reserved = 0,
  limit,
  showLegend = true,
  className,
  ...props
}: BudgetOrbitProps) {
  const safeLimit = Math.max(1, limit);
  const cx = 110;
  const cy = 110;

  const usedRatio = Math.min(Math.max(0, used) / safeLimit, 1);
  const projectedRatio = Math.min(Math.max(0, projected) / safeLimit, 1);
  const reservedRatio = Math.min(Math.max(0, reserved) / safeLimit, 1);

  const usedPct = Math.round(usedRatio * 100);

  const rings = [
    {
      id: "used",
      label: "Used",
      radius: 92,
      ratio: usedRatio,
      color: "var(--color-chart-1, var(--chart-1, hsl(var(--primary))))",
      bgClass: "text-primary",
    },
    {
      id: "projected",
      label: "Projected",
      radius: 72,
      ratio: projectedRatio,
      color: "var(--color-chart-2, var(--chart-2, hsl(var(--accent))))",
      bgClass: "text-accent",
    },
    {
      id: "reserved",
      label: "Reserved",
      radius: 52,
      ratio: reservedRatio,
      color: "var(--color-chart-3, var(--chart-3, hsl(var(--info))))",
      bgClass: "text-info",
    },
  ];

  return (
    <div className={cn("flex flex-col items-center gap-3", className)} {...props}>
      <svg
        viewBox="0 0 220 220"
        className="size-52 max-w-full"
        role="img"
        aria-label={`Budget orbit: ${usedPct}% of ${formatCreditsUsd(limit)} limit used`}
      >
        {rings.map((ring) => {
          const circumference = 2 * Math.PI * ring.radius;
          const strokeDash = `${(circumference * ring.ratio).toFixed(1)} ${circumference.toFixed(1)}`;
          return (
            <g key={ring.id}>
              {/* Background track */}
              <circle
                cx={cx}
                cy={cy}
                r={ring.radius}
                fill="none"
                stroke="currentColor"
                className="text-muted/30"
                strokeWidth={6}
              />
              {/* Value stroke */}
              <circle
                cx={cx}
                cy={cy}
                r={ring.radius}
                fill="none"
                stroke={ring.color}
                strokeWidth={6}
                strokeLinecap="round"
                strokeDasharray={strokeDash}
                transform={`rotate(-90 ${cx} ${cy})`}
                className="transition-all duration-500 ease-out"
              />
            </g>
          );
        })}
        {/* Center label */}
        <text
          x={cx}
          y={cy + 2}
          textAnchor="middle"
          className="fill-foreground text-2xl font-semibold tracking-tight"
        >
          {usedPct}%
        </text>
        <text
          x={cx}
          y={cy + 20}
          textAnchor="middle"
          className="fill-muted-foreground text-xs font-medium tracking-wider uppercase"
        >
          OF {formatCreditsUsd(limit)}
        </text>
      </svg>

      {showLegend ? (
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span
              className="size-2 rounded-full"
              style={{
                backgroundColor: "var(--color-chart-1, var(--chart-1, hsl(var(--primary))))",
              }}
            />
            Used ({usedPct}%)
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="size-2 rounded-full"
              style={{
                backgroundColor: "var(--color-chart-2, var(--chart-2, hsl(var(--accent))))",
              }}
            />
            Projected ({Math.round(projectedRatio * 100)}%)
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="size-2 rounded-full"
              style={{
                backgroundColor: "var(--color-chart-3, var(--chart-3, hsl(var(--info))))",
              }}
            />
            Reserved ({Math.round(reservedRatio * 100)}%)
          </span>
        </div>
      ) : null}
    </div>
  );
}
